import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users,
  FolderKanban,
  Server,
  Database,
  ListTodo,
  Tag,
  Package,
  Play,
  Mail,
  Wrench,
  BarChart3,
} from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchAdminMetrics } from '@/api/admin';

interface NavItem {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const navItems: NavItem[] = [
  {
    title: 'Users',
    description: 'Manage user accounts',
    href: '/admin/users',
    icon: Users,
    color: 'bg-blue-100 text-blue-600',
  },
  {
    title: 'Shared Servers',
    description: 'Manage development servers',
    href: '/admin/shared-servers',
    icon: Server,
    color: 'bg-sky-100 text-sky-600',
  },
  {
    title: 'B2 Credentials',
    description: 'Backblaze backup credentials',
    href: '/admin/b2',
    icon: Database,
    color: 'bg-green-100 text-green-600',
  },
  {
    title: 'Tasks',
    description: 'View background tasks',
    href: '/admin/tasks',
    icon: ListTodo,
    color: 'bg-yellow-100 text-yellow-600',
  },
  {
    title: 'Test Tags',
    description: 'Manage Odoo test tags',
    href: '/admin/test-tags',
    icon: Tag,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    title: 'Odoo Versions',
    description: 'Configure version builds',
    href: '/admin/odoo-versions',
    icon: Package,
    color: 'bg-orange-100 text-orange-600',
  },
  {
    title: 'Continuous Testing',
    description: 'Automated test scheduling',
    href: '/admin/continuous-testing',
    icon: Play,
    color: 'bg-cyan-100 text-cyan-600',
  },
  {
    title: 'Email Tools',
    description: 'Test email templates',
    href: '/admin/email-tools',
    icon: Mail,
    color: 'bg-pink-100 text-pink-600',
  },
  {
    title: 'Playbook Runner',
    description: 'Execute Ansible playbooks',
    href: '/admin/playbook-runner',
    icon: Wrench,
    color: 'bg-red-100 text-red-600',
  },
  {
    title: 'Project Tools',
    description: 'Project maintenance',
    href: '/admin/project-tools',
    icon: FolderKanban,
    color: 'bg-indigo-100 text-indigo-600',
  },
];

export function AdminDashboardPage() {
  const { data: metrics } = useQuery({
    queryKey: ['admin-metrics'],
    queryFn: fetchAdminMetrics,
  });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">System overview and quick access to admin tools</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Users</p>
                <p className="text-2xl font-semibold">{metrics?.users ?? '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FolderKanban className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Projects</p>
                <p className="text-2xl font-semibold">{metrics?.projects ?? '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Teams</p>
                <p className="text-2xl font-semibold">{metrics?.teams ?? '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Admin Tools</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className={`p-2 rounded-lg ${item.color}`}>
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-medium">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
